import { useEffect, useRef } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useBlocker } from 'react-router';

import { MODAL_UNSAVED_CHANGES } from '@/components/organisms/Modals/ModalConductor';
import { resetAllFormStates } from '@/redux/forms/actions';
import { selectAnyFormDirty } from '@/redux/forms/selectors';
import { pushModal } from '@/redux/modals/actions';
import { RootState } from '@/shared/types/state';

// Blocks navigation while any form is dirty; pass `onSave` for a "Save" modal primary (else "Go back").
// `onSave` returning true means it navigated itself. Pass `compareSearch` when the query string
// switches what the page shows, so a query-only navigation is blocked too.
export const useDirtyFormBlocker = (
  onSave?: () => boolean | void | Promise<boolean | void>,
  { compareSearch = false }: { compareSearch?: boolean } = {}
) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const isAnyFormDirty = useSelector(selectAnyFormDirty);
  // react-router registers this callback in an effect, so a closure over `isAnyFormDirty` is one
  // render stale. Reading the store here lets a caller clear the form state and navigate right away.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      selectAnyFormDirty(store.getState()) &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (compareSearch && currentLocation.search !== nextLocation.search))
  );

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  // Prevents re-pushing while the blocker is still 'blocked' after the modal closes.
  const hasPromptedRef = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      hasPromptedRef.current = false;
      return;
    }
    if (isAnyFormDirty && !hasPromptedRef.current) {
      hasPromptedRef.current = true;
      const modalProps = {
        onSave: onSaveRef.current
          ? async () => {
              const hasNavigated = await onSaveRef.current?.();
              if (!hasNavigated) {
                blockerRef.current.proceed?.();
              }
            }
          : undefined,
        onDiscard: () => {
          dispatch(resetAllFormStates());
          blockerRef.current.proceed?.();
        },
        onCancel: () => blockerRef.current.reset?.(),
      };
      dispatch(pushModal(MODAL_UNSAVED_CHANGES, modalProps));
    }
  }, [blocker.state, dispatch, isAnyFormDirty]);

  return blocker;
};
